import React, { useEffect, useState } from 'react';
import PatientCareCirclecard from './PatientCareCircleCard';
import { Popconfirm, Space, Spin, Tooltip, Typography } from 'antd';
import IntentType from '../../../../../../intentexecutor/utils/IntentType';
import { executeIntent } from '../../../../../utils/IntentExecutor';
import { useRecoilValue } from 'recoil';
import { currentConfiguration } from '../../../../../../service/memory/recoil/configurationstore';
import { useNavigate } from 'react-router-dom';
import { useGetRole } from '../../../../../generic/hooks/useGetRole';
import { useReadMemory } from '../../../../../generic/hooks/useReadMemory';
import MemoryType from '../../../../../../service/memory/enum/MemoryType';
import { useObservable } from '../../../../../generic/hooks/useObservable';
import ConfigurationConstants from '../../../../../utils/configurationconstants';
import { notifiyUser } from '../../../../../utils/Notification';
import { strings } from '../../../../../utils/strings';
import { RoleToLinkMapper } from '../../../../../utils/UrlUtils';
import { QuestionCircleOutlined } from '@ant-design/icons';
import NoDataMessageComponent from '../../../../../../components/generic/nodatamessage/NoDataMessageComponent';
import { teamMemberApiResponse } from '../../../../../organizationsettings/patientcarecircle/addedit/AddEditTeamMemberModalComponent';
import "../../styles/visitdetails.css"
import SmartIcon from '../../../../../../components/generic/genericIcon/SmartIcon';

const { Text } = Typography;

const PatientCareCircleComponent = ({ identifier, showOnProfilePage, setGroupIdentifier, setSelectedCareCircleTeamMembers, setExistingMembers }) => {
    const [careCircleTeamMembers, setCareCircleTeamMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const navigate = useNavigate();
    const role = useGetRole();
    const userJson = useReadMemory(MemoryType.USER);

    const [refresh, setRefresh] = useState(false);
    useObservable(teamMemberApiResponse, setRefresh);

    const configuration = useRecoilValue(currentConfiguration);

    const [showCallIcon, setShowCallIcon] = useState(false);
    const [showProfileIcon, setShowProfileIcon] = useState(false);
    const [showDeleteIcon, setShowDeleteIcon] = useState(false);


    const getCareCircleMembers = async () => {
        setIsLoading(true);
        //get groupIdentifier for a particular patient
        let payload = {
            group: {
                namespace: `com.niramaya.patient.${identifier}.carecircle`
            }
        }
        let result = await executeIntent(IntentType.GET_GROUP_BY_NAMESPACE, payload);

        //get the group members using the groupIdentifier
        if (result?.length > 0) {
            const safeSetGroupIdentifier = setGroupIdentifier || (() => {});
            const safeSetSelectedCareCircleTeamMembers = setSelectedCareCircleTeamMembers || (() => {});
            if (showOnProfilePage) safeSetGroupIdentifier(result[0]?.groupIdentifier);
            payload = {
                group: {
                    groupIdentifier: result[0]?.groupIdentifier
                }
            }
            result = await executeIntent(IntentType.GET_GROUP_MEMBERS_DETAILS, payload);
            result = result?.map(data => ({ ...data, userIdentifier: data?.entityIdentifier }));
            if (showOnProfilePage) safeSetSelectedCareCircleTeamMembers(result);
            setCareCircleTeamMembers(result);
            setExistingMembers(result);
        } else {
            setCareCircleTeamMembers([]);
        }
        setIsLoading(false);
        setRefresh(false);
    }

    const handleClickViewProfile = (identifier) => {
        navigate(RoleToLinkMapper(role) + "volunteer-profile?identifier=" + identifier);
    }

    const handleDeleteTeamMember = async (teamMember) => {   
        setIsDataLoading(true);
        const payload = {
          memberships: [
            {
              membershipIdentifier: teamMember?.membershipIdentifier,
            }
          ],
          creatorIdentifier: userJson?.userIdentifier,
        }
        const result = await executeIntent(IntentType.DELETE_GROUP_MEMBERSHIP, payload);
        setIsDataLoading(false);
        notifiyUser(result, strings.CARE_TEAM_MEMBER_DELETED_SUCCESS, strings.CARE_TEAM_MEMBER_DELETED_ERROR);
        setRefresh(true);
      }

    const additionalActions = (teamMember) => (
        <div>
            <Space size={10}>
                {showCallIcon && (
                    <>
                        <Tooltip title={teamMember?.telephone ? `Call ${teamMember?.telephone}` : ""} placement="bottom">
                            <>
                                <SmartIcon iconKey={"call"} height="16" width="16" />
                            </>
                        </Tooltip>
                    </>
                )}
                {showProfileIcon && (
                    <>
                        <Tooltip title="View Profile" placement="bottom">
                            <>
                                <SmartIcon iconKey={"profile"} height={20} width={20} handleIconClick={() => handleClickViewProfile(teamMember?.entityIdentifier)} />
                            </>
                        </Tooltip>
                    </>
                )}
                {showDeleteIcon && (
                    <Popconfirm
                        placement="topRight"
                        title={<Text>Are you sure you want to remove <Text strong>{teamMember?.givenName} {teamMember?.familyName}</Text> from Care Circle?</Text>}
                        overlayClassName='carecircle-team-delete-popconfirm'
                        icon={
                        <QuestionCircleOutlined
                            style={{
                            color: 'red',
                            }}
                        />
                        }
                        onConfirm={() => handleDeleteTeamMember(teamMember)}
                        okButtonProps={{
                        loading: isDataLoading,
                        }}>
                        <Tooltip title="Remove" placement="bottom">
                            <>
                                <SmartIcon iconKey={"delete"} height={20} width={20} />
                            </>
                        </Tooltip>
                    </Popconfirm>
                )}
            </Space>
        </div>
    )

    useEffect(() => {
        // access control for call icon
        const configurationShowMemberCallIcon = configuration[ConfigurationConstants.FEATUREACCESS_PATIENT_CARECIRCLE_ALLOW_CALL]
        setShowCallIcon(configurationShowMemberCallIcon?.visibility)
    
        // access control for profile visit icon
        const configurationShowMemberProfileVisitIcon = configuration[ConfigurationConstants.FEATUREACCESS_PATIENT_CARECIRCLE_ALLOW_MEMBER_PROFILE_VISIT]
        setShowProfileIcon(configurationShowMemberProfileVisitIcon?.visibility)
    
        // access control for member delete
        const configurationShowMemberDeleteIcon = configuration[ConfigurationConstants.FEATUREACCESS_PATIENT_CARECIRCLE_ALLOW_MEMBER_DELETE]
        setShowDeleteIcon(configurationShowMemberDeleteIcon?.visibility)
    
      }, [configuration])

    useEffect(() => {
        getCareCircleMembers();
    }, [identifier, refresh])

    return (
        <>
            {isLoading && <Spin size="medium" className="d-flex justify-content-center m-auto" />}
            {!isLoading && (!careCircleTeamMembers || careCircleTeamMembers?.length === 0) && (
                <NoDataMessageComponent text="No data found!" />
            )}
            {!isLoading && careCircleTeamMembers?.length > 0 && (
                careCircleTeamMembers?.map(member => (
                    <div style={{ margin: "16px 0" }}>
                        <PatientCareCirclecard member={member} key={member?.membershipIdentifier} showOnProfilePage={showOnProfilePage} additionalActions={additionalActions} />
                    </div>
                ))
            )}
        </>
    );
}

export default PatientCareCircleComponent;